<template>
	<p>{{ text.join('') }}</p>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const { language } = storeToRefs(useLanguageStore());

const props = defineProps<{
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
}>();

const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Tällä hetkellä voit katsoa elokuvan',
					'suoratoistona palvelussa'
				),
				getOfferTextBlock(MonetizationType.Buy, ' tai ostaa sen digitaalisena versiona palvelussa'),
				'. ',
			];
		case 'ru':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В настоящее время Вы можете посмотреть', 'на'),
				getOfferTextBlock(MonetizationType.Buy, ' или купить и скачать на'),
				'. ',
			];
		case 'it':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Adesso puoi guardare', 'in streaming su'),
				getOfferTextBlock(MonetizationType.Buy, ' o comprarlo scaricandolo su'),
				'. ',
			];
		case 'de':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Du kannst', 'bei', 'legal im Stream anschauen'),
				getOfferTextBlock(MonetizationType.Buy, ' oder bei', 'als Download kaufen'),
				'. ',
			];
		case 'fr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'En ce moment, vous pouvez regarder', 'en streaming sur'),
				getOfferTextBlock(MonetizationType.Buy, ' ou l`acheter en téléchargement sur'),
				'. ',
			];
		case 'es':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Actualmente, usted es capaz de ver', 'streaming en'),
				getOfferTextBlock(MonetizationType.Buy, ' o comprarlo como descarga en'),
				'. ',
			];
		case 'ja':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'は', 'で配信中です。'),
				getOfferTextBlock(MonetizationType.Buy, '配信版を購入してダウンロードする場合は', 'です'),
				'。',
			];
		case 'ko':
			return [
				getOfferTextBlock(MonetizationType.Flatrate, '현재 ', `에서 "${props.title}" 스트리밍 서비스 중이며 `),
				getOfferTextBlock(MonetizationType.Buy, '', '에서 유료 다운로드하실 수 있습니다.'),
			];
		case 'ro':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'În prezent, puteți viziona', 'streaming pe'),
				getOfferTextBlock(MonetizationType.Buy, ' sau să-l cumpărați prin descărcare pe'),
				'. ',
			];
		case 'hu':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Jelenleg a(z)', 'adatfolyamként elérhető a(z)'),
				getOfferTextBlock(MonetizationType.Buy, ' szolgáltatónál, vagy letölthető innen:'),
				'. ',
			];
		case 'cs':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktuálně máte možnost sledovat', 'streaming u'),
				getOfferTextBlock(MonetizationType.Buy, ' nebo si jej zakoupit ke stažení u'),
				'. ',
			];
		case 'bg':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В момента можете да гледате стрийминг на', 'в', 'онлайн'),
				getOfferTextBlock(MonetizationType.Buy, ' или да го купите като изтеглена версия в'),
				'. ',
			];
		case 'ar':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'يمكنكم حالياً مشاهدة', 'على'),
				getOfferTextBlock(MonetizationType.Buy, ' أو شرائه وتنزيله على'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Atualmente você pode assistir', 'em streaming via'),
				getOfferTextBlock(MonetizationType.Buy, ' ou comprar como download nas plataformas:'),
				'. ',
			];
		case 'tr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Şu an', 'adlı yapımı'),
				getOfferTextBlock(
					MonetizationType.Buy,
					' üzerinden izleyebilir veya',
					'üzerinden satın alarak indirebilirsiniz'
				),
				'. ',
			];
		case 'sq':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktualisht, ju mund të shikoni', 'në'),
				getOfferTextBlock(MonetizationType.Buy, ' ose ta blini si shkarkim në'),
				'. ',
			];
		case 'pl':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Obecnie możesz oglądać', 'w streamingu w serwisie'),
				getOfferTextBlock(MonetizationType.Buy, ' lub kupić go jako plik do pobrania w serwisie'),
				'. ',
			];
		case 'az':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Hazırda siz', 'üzərindən'),
				getOfferTextBlock(
					MonetizationType.Buy,
					' yayımını izləyə və ya onu',
					'vasitəsilə alaraq endirə bilərsiniz'
				),
				'. ',
			];
		case 'el':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
					'με streaming στο'
				),
				getOfferTextBlock(MonetizationType.Buy, ' ή να τον αγοράσετε ως αρχείο λήψης στο'),
				'. ',
			];
		case 'sr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Trenutno možete da gledate', 'strim na'),
				getOfferTextBlock(MonetizationType.Buy, ' ili da ga kupite kao preuzimanje na'),
				'. ',
			];
		case 'sv':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'För närvarande kan du se', 'streama på'),
				getOfferTextBlock(MonetizationType.Buy, ' eller köpa den som nedladdning på'),
				'. ',
			];
		case 'uk':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Наразі ви можете дивитися трансляцію', 'на'),
				getOfferTextBlock(MonetizationType.Buy, ' або придбати і завантажити її на'),
				'. ',
			];
		case 'ur':
			return [
				getRTLInfoTextBlock(MonetizationType.Flatrate, 'فی الحال آپ ', 'پر'),
				getOfferTextBlock(
					MonetizationType.Buy,
					'اسٹریمنگ دیکھنے کے قابل ہیں یا اسے',
					'پر ڈاؤن لوڈ کے طور پر خرید سکتے ہیں۔'
				),
			];
		case 'zh':
			return [
				getRTLInfoTextBlock(MonetizationType.Flatrate, '目前你可以在', '观看', ','),
				getOfferTextBlock(MonetizationType.Buy, ' 或在', '上购买下载'),
			];
		case 'en':
		default:
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Currently you are able to watch', 'streaming on'),
				getOfferTextBlock(MonetizationType.Buy, ' or buy it as download on'),
				'. ',
			];
	}
});
</script>
